// AI Message Component — OFFSET
// Message utilisateur : bloc bg-secondary aligné à droite, radius 0.
// Réponse IA : texte nu sans bulle, px-mark 16px en tête + tag mono.

import React, { memo } from 'react';
import { cn } from '@/lib/utils';
import { PxMark } from '@/components/PxMark';

export interface AIMessageProps {
    from: 'user' | 'assistant' | 'system';
    variant?: 'contained' | 'flat';
    avatar?: {
        src?: string;
        name?: string;
        icon?: React.ReactNode;
    };
    className?: string;
    children: React.ReactNode;
}

export const AIMessage = memo(({
    from,
    variant = 'contained',
    avatar,
    className,
    children
}: AIMessageProps) => {
    const isUser = from === 'user';

    if (isUser) {
        return (
            <div
                data-from={from}
                className={cn("flex w-full justify-end py-1.5", className)}
            >
                <div className="max-w-[85%] bg-secondary px-3.5 py-2.5 text-sm leading-relaxed text-foreground">
                    {children}
                </div>
            </div>
        );
    }

    return (
        <div data-from={from} className={cn("w-full py-1.5", className)}>
            {/* Tête : px-mark + tag mono */}
            <div className="mb-2 flex items-center gap-2">
                <PxMark size={16} state="idle" label="Pixel" />
                <span className="font-mono text-[10px] uppercase tracking-wider text-faint">
                    Pixel
                </span>
            </div>

            {/* Contenu nu, sans bulle */}
            <AIMessageContent variant={variant}>
                {children}
            </AIMessageContent>
        </div>
    );
});

AIMessage.displayName = 'AIMessage';

// Message Content wrapper
export interface AIMessageContentProps {
    variant?: 'contained' | 'flat';
    className?: string;
    children: React.ReactNode;
}

export const AIMessageContent = memo(({
    variant = 'contained',
    className,
    children
}: AIMessageContentProps) => {
    return (
        <div
            className={cn(
                "flex flex-col gap-1 overflow-hidden text-sm leading-relaxed text-foreground",
                variant === 'contained' && "max-w-[85%]",
                variant === 'flat' && "max-w-full",
                className
            )}
        >
            {children}
        </div>
    );
});

AIMessageContent.displayName = 'AIMessageContent';

// Pre-styled message response (for markdown content)
export interface AIMessageResponseProps {
    className?: string;
    children: React.ReactNode;
}

export const AIMessageResponse = memo(({
    className,
    children
}: AIMessageResponseProps) => {
    return (
        <div className={cn("max-w-none text-sm leading-relaxed text-foreground", className)}>
            {children}
        </div>
    );
});

AIMessageResponse.displayName = 'AIMessageResponse';

export default AIMessage;
